import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges
} from '@angular/core';
import {Subscription} from 'rxjs';
import {PagamentosEletronicosService} from '../../services/pagamentos-eletronicos.service';
import {SocketService} from '../../services/socket.service';
import {PagamentoEletronicoFluxo} from './pagamento-eletronico-fluxo';

@Component({
  selector: 'app-pagamento-eletronico-dialog',
  templateUrl: './pagamento-eletronico-dialog.component.html',
  styleUrls: ['./pagamento-eletronico-dialog.component.scss']
})
export class PagamentoEletronicoDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() aberto = false;
  @Input() pedido: any;
  @Input() comanda: any;
  @Input() pagamentos: any[] = [];
  @Output() finalizado = new EventEmitter<any>();
  @Output() fechado = new EventEmitter<any>();

  fluxo = new PagamentoEletronicoFluxo();
  terminais: any[] = [];
  terminalSelecionado: any;
  carregando = false;
  cobrandoPagamentoId: number;
  erro: string;
  private socketSubscription: Subscription;
  private polling: any;
  private finalizacaoEmitida = false;

  constructor(private pagamentosEletronicosService: PagamentosEletronicosService,
              private socketService: SocketService) {}

  ngOnInit(): void {
    this.socketSubscription = this.socketService.on('connecttef_pagamento_atualizado')
      .subscribe((evento: any) => {
        if(!this.aberto || !this.eventoDaOrigem(evento)) return;
        this.atualizeTransacoesAtivas();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.aberto && this.aberto) this.carregue();
  }

  ngOnDestroy(): void {
    if(this.socketSubscription) this.socketSubscription.unsubscribe();
    this.parePolling();
  }

  async carregue(): Promise<void> {
    if(!this.aberto) return;
    this.carregando = true;
    this.erro = null;
    this.finalizacaoEmitida = false;
    try {
      const [terminais, mapeamentos] = await Promise.all([
        this.pagamentosEletronicosService.listeTerminais(),
        this.pagamentosEletronicosService.listeFormasPagamento(),
      ]);
      this.terminais = (terminais || []).filter((terminal: any) =>
        terminal && terminal.ativo === true && terminal.statusVinculo === 'LINKED' &&
        !terminal.desativadoEm
      );
      if(!this.terminalSelecionado || !this.terminais.some(item => item.id === this.terminalSelecionado.id))
        this.terminalSelecionado = this.terminais.length === 1 ? this.terminais[0] : null;
      this.fluxo = new PagamentoEletronicoFluxo(this.pagamentos || [], mapeamentos || []);

      this.apliqueHistorico(await this.listeHistoricoOrigem());
      this.iniciePollingSeNecessario();
    } catch(erro) {
      this.erro = this.obtenhaMensagem(erro);
    } finally {
      this.carregando = false;
    }
  }

  async cobre(pagamento: any): Promise<void> {
    if(this.cobrandoPagamentoId || !pagamento) return;
    this.erro = null;
    this.cobrandoPagamentoId = pagamento.id;
    try {
      const solicitacao = this.fluxo.monteSolicitacao(this.terminalSelecionado, pagamento);
      const chave = this.crieChaveIdempotencia(pagamento.id);
      const transacao = this.pedido && this.pedido.id ?
        await this.pagamentosEletronicosService.cobrePagamentoPedido(
          this.pedido.id, pagamento.id, solicitacao.terminalId,
          solicitacao.tipoConnectTef, chave
        ) : await this.pagamentosEletronicosService.cobrePagamentoComanda(
          this.comanda.id, pagamento.id, solicitacao.terminalId,
          solicitacao.tipoConnectTef, chave
        );
      this.fluxo.apliqueTransacao(pagamento.id, transacao);
      this.emitaFinalizacaoSeConcluida();
      this.iniciePollingSeNecessario();
    } catch(erro) {
      this.erro = this.obtenhaMensagem(erro);
      try {
        this.apliqueHistorico(await this.listeHistoricoOrigem());
      } catch(erroConsulta) {}
      const atual = this.fluxo.obtenhaTransacao(pagamento);
      if(!atual || ['REJECTED', 'ERROR'].indexOf(atual.status) !== -1) {
        const status = erro && erro.definitivo ? 'ERROR' : 'INDETERMINATE';
        this.fluxo.apliqueTransacao(pagamento.id, {status, erro: this.erro});
      }
      this.iniciePollingSeNecessario();
    } finally {
      this.cobrandoPagamentoId = null;
    }
  }

  async atualizeTransacao(pagamento: any): Promise<void> {
    const atual = this.fluxo.obtenhaTransacao(pagamento);
    if(!atual || !atual.identificacao) return;
    try {
      const transacao = await this.pagamentosEletronicosService.obtenhaTransacao(atual.identificacao);
      this.fluxo.apliqueTransacao(pagamento.id, transacao);
      this.erro = null;
      this.emitaFinalizacaoSeConcluida();
      this.iniciePollingSeNecessario();
    } catch(erro) {
      this.erro = this.obtenhaMensagem(erro);
    }
  }

  podeTentarNovamente(pagamento: any): boolean {
    return this.fluxo.podeTentarNovamente(pagamento);
  }

  get podeFechar(): boolean {
    return !this.carregando && !this.cobrandoPagamentoId && this.fluxo.podeFechar;
  }

  get pagamentosEletronicos(): any[] {
    return this.fluxo.listePagamentosEletronicos();
  }

  get possuiTransacaoAtiva(): boolean {
    return !this.fluxo.podeFechar;
  }

  feche(): void {
    if(!this.podeFechar) return;
    this.parePolling();
    this.fechado.emit({concluido: this.fluxo.concluido});
  }

  textoStatus(pagamento: any): string {
    const transacao = this.fluxo.obtenhaTransacao(pagamento);
    const textos: {[status: string]: string} = {
      CREATED: 'Preparando cobrança',
      SENT: 'Aguardando pagamento no terminal',
      APPROVED: 'Pagamento aprovado',
      REJECTED: 'Pagamento rejeitado',
      ERROR: 'Falha ao iniciar a cobrança',
      INDETERMINATE: 'Resultado indefinido — aguarde a confirmação',
    };
    return transacao ? textos[transacao.status] || transacao.status : 'Aguardando início';
  }

  textoFiscal(pagamento: any): string {
    const transacao = this.fluxo.obtenhaTransacao(pagamento);
    if(!transacao || transacao.status !== 'APPROVED') return '';
    const status = transacao.statusFiscal;
    if(status === 'AUTHORIZED') return 'NFC-e autorizada.';
    if(status === 'NOT_APPLICABLE') return 'Pagamento concluído sem integração fiscal.';
    if(status === 'GENERATED') return 'NFC-e gerada; aguarde a autorização fiscal.';
    if(status === 'FISCAL_ERROR' || status === 'BLOCKED_ACQUIRER_MAPPING')
      return 'Pagamento aprovado, com pendência fiscal. Consulte o detalhe do pedido.';
    return 'Pagamento aprovado. A NFC-e ainda não foi enviada; use o botão Enviar quando o envio automático estiver desativado.';
  }

  private apliqueHistorico(transacoes: any[]): void {
    const pagamentos = this.pagamentosEletronicos;
    (transacoes || []).forEach((transacao: any) => {
      const pagamento = pagamentos.find((item: any) =>
        Number(item.id) === Number(transacao.pagamentoId || transacao.idPagamento)
      );
      if(pagamento) this.fluxo.apliqueTransacao(pagamento.id, transacao);
    });
  }

  async atualizeTransacoesAtivas(): Promise<void> {
    try {
      this.apliqueHistorico(await this.listeHistoricoOrigem());
      this.erro = null;
    } catch(erro) {
      this.erro = this.obtenhaMensagem(erro);
    }
    const ativas = this.pagamentosEletronicos.filter((pagamento: any) => {
      const transacao = this.fluxo.obtenhaTransacao(pagamento);
      return transacao && !!transacao.identificacao && (
        ['CREATED', 'SENT', 'INDETERMINATE', 'REVERSAL_PENDING'].indexOf(transacao.status) !== -1 ||
        (transacao.status === 'APPROVED' && !this.transacaoFinalizada(transacao))
      );
    });
    await Promise.all(ativas.map((pagamento: any) => this.atualizeTransacao(pagamento)));
  }

  private listeHistoricoOrigem(): Promise<any[]> {
    if(this.pedido && this.pedido.id)
      return this.pagamentosEletronicosService.listeTransacoesPedido(this.pedido.id);
    if(this.comanda && this.comanda.id)
      return this.pagamentosEletronicosService.listeTransacoesComanda(this.comanda.id);
    return Promise.resolve([]);
  }

  private transacaoFinalizada(transacao: any): boolean {
    if(!transacao || transacao.posProcessamentoPendente === true) return false;
    return !!transacao.finalizadoEm ||
      ['READY', 'GENERATED', 'AUTHORIZED', 'FISCAL_ERROR', 'BLOCKED_ACQUIRER_MAPPING']
        .indexOf(transacao.statusFiscal) !== -1;
  }

  private iniciePollingSeNecessario(): void {
    if(this.fluxo.podeFechar) {
      this.parePolling();
      return;
    }
    if(this.polling || typeof window === 'undefined') return;
    this.polling = window.setInterval(() => this.atualizeTransacoesAtivas(), 2500);
  }

  private parePolling(): void {
    if(this.polling && typeof window !== 'undefined') window.clearInterval(this.polling);
    this.polling = null;
  }

  private emitaFinalizacaoSeConcluida(): void {
    if(!this.fluxo.concluido || this.finalizacaoEmitida) return;
    this.finalizacaoEmitida = true;
    this.parePolling();
    this.finalizado.emit({pedido: this.pedido, comanda: this.comanda});
  }

  private eventoDaOrigem(evento: any): boolean {
    return !!evento && (
      (this.pedido && Number(evento.pedidoId) === Number(this.pedido.id)) ||
      (this.comanda && Number(evento.comandaId) === Number(this.comanda.id))
    );
  }

  private crieChaveIdempotencia(idPagamento: number): string {
    return `connecttef-${idPagamento}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }

  private obtenhaMensagem(erro: any): string {
    return String(erro && (erro.mensagem || erro.erro || erro.message) || erro ||
      'Falha ao comunicar com o ConnectTEF');
  }
}
